import { useMemo } from 'react';
import { colors } from '../colors/colors';
import { ConfigLookup, PaletteTokens, toCssVar } from './color-sound-config-resolver';
import { colorSoundConfigStore } from './color-sound-config-store';
import { usePriorityColor } from './use-configured-colors';

interface UnassignedTimerState {
    maxUnassignedTimeReached?: boolean;
    assignedUnits?: { isHeld?: boolean }[] | null;
}

/** An event flashes once its unassigned timer strikes, until a unit that isn't held is assigned. */
export function isUnassignedTimerFlashing(event: UnassignedTimerState): boolean {
    const isAssigned = (event.assignedUnits ?? []).some((unit) => !unit.isHeld);
    return !!event.maxUnassignedTimeReached && !isAssigned;
}

export interface PrioritySolidFill {
    background: string;
    text?: string;
}

/**
 * The fill of a priority that isn't flashing. An unconfigured priority looks as it always has: P1 red
 * with the row's own text color, everything else unfilled.
 */
export function getPrioritySolidFill(
    configuredColor: ConfigLookup<PaletteTokens>,
    isPriority1: boolean | undefined
): PrioritySolidFill | null {
    if (configuredColor.matched) {
        return configuredColor.value
            ? {
                  background: toCssVar(configuredColor.value.baseToken, configuredColor.value.hex),
                  text: toCssVar(configuredColor.value.contentToken),
              }
            : null;
    }
    return isPriority1 ? { background: colors.queuePriority } : null;
}

export interface FrPriorityAppearance {
    isFlashing: boolean;
    /** Configured priorities are bold; unconfigured ones keep today's weight. */
    isBold?: boolean;
    // Only set while flashing. Undefined keeps today's red flash.
    flashColor?: string;
    fill?: string;
    text?: string;
}

/**
 * The #rrggbb color a priority flashes in. Undefined keeps today's red flash for an unconfigured
 * priority. The flash is an alert, so an explicit "no color" flashes gray rather than not at all.
 */
export function getPriorityFlashColor(
    configuredColor: ConfigLookup<PaletteTokens>
): string | undefined {
    if (!configuredColor.matched) {
        return undefined;
    }
    return configuredColor.value ? configuredColor.value.hex : colors.priorityNoColorFlash;
}

interface FrPriorityEvent extends UnassignedTimerState {
    priorityId?: number | null;
    agencyTypeGlobalAttrId?: number | null;
    isPriority1?: boolean;
}

/**
 * Once a department configures priority colors, First Responder follows the CAD priority cell: it
 * flashes on the unassigned timer, not for P1. Until then (and with the flag off) P1 flashes the way
 * it always has, even with no unassigned timer and even once assigned, which every event in the FR
 * units list is.
 */
export function getFrPriorityAppearance(
    configuredColor: ConfigLookup<PaletteTokens>,
    event: FrPriorityEvent,
    hasConfiguredPriorityColors: boolean
): FrPriorityAppearance {
    if (!hasConfiguredPriorityColors) {
        return { isFlashing: !!event.isPriority1 };
    }
    const isBold = configuredColor.matched;
    if (isUnassignedTimerFlashing(event)) {
        const flashColor = getPriorityFlashColor(configuredColor);
        return flashColor ? { isFlashing: true, flashColor, isBold } : { isFlashing: true, isBold };
    }
    const solidFill = getPrioritySolidFill(configuredColor, event.isPriority1);
    return solidFill
        ? { isFlashing: false, fill: solidFill.background, text: solidFill.text, isBold }
        : { isFlashing: false, isBold };
}

// Reads a MobX observable, so callers must be `observer` components.
export function useFrPriorityAppearance(
    event: FrPriorityEvent | null | undefined
): FrPriorityAppearance {
    const configuredColor = usePriorityColor(
        event?.priorityId ?? null,
        event?.agencyTypeGlobalAttrId
    );
    const maxUnassignedTimeReached = event?.maxUnassignedTimeReached;
    const assignedUnits = event?.assignedUnits;
    const isPriority1 = event?.isPriority1;
    const hasConfiguredPriorityColors = colorSoundConfigStore.hasPriorityColors;
    return useMemo(
        function resolveFrPriorityAppearance() {
            return getFrPriorityAppearance(
                configuredColor,
                { maxUnassignedTimeReached, assignedUnits, isPriority1 },
                hasConfiguredPriorityColors
            );
        },
        [
            configuredColor,
            maxUnassignedTimeReached,
            assignedUnits,
            isPriority1,
            hasConfiguredPriorityColors,
        ]
    );
}

export interface PriorityLabelColors {
    fill: string;
    text?: string;
}

/** For priority labels that always have a fill today. An explicit "no color" drops the fill. */
export function getPriorityLabelColors(
    configuredColor: ConfigLookup<PaletteTokens>,
    defaultColors: PriorityLabelColors
): PriorityLabelColors {
    if (!configuredColor.matched) {
        return defaultColors;
    }
    return configuredColor.value
        ? {
              fill: toCssVar(configuredColor.value.baseToken, configuredColor.value.hex),
              text: toCssVar(configuredColor.value.contentToken),
          }
        : { fill: '' };
}

export interface PriorityCautionAppearance {
    priority: 1 | 4;
    style?: { background?: string; color?: string; fontWeight: 'bold' };
}

/**
 * For priorities shown as an Arc `Caution`. Unconfigured, P1 is the red caution and everything else
 * the neutral one, as before. A configured priority is bold and takes its configured fill on the
 * neutral caution; an explicit "no color" leaves it neutral.
 */
export function getPriorityCautionAppearance(
    configuredColor: ConfigLookup<PaletteTokens>,
    isPriority1: boolean
): PriorityCautionAppearance {
    if (!configuredColor.matched) {
        return { priority: isPriority1 ? 1 : 4 };
    }
    const solidFill = getPrioritySolidFill(configuredColor, isPriority1);
    return {
        priority: 4,
        style: solidFill
            ? { background: solidFill.background, color: solidFill.text, fontWeight: 'bold' }
            : { fontWeight: 'bold' },
    };
}
